export type CityTheme = 'boston' | 'northend' | 'harbor' | 'cambridge' | 'sports' | 'travel' | 'food' | 'fallout';

export interface NavSection {
  id: string;
  label: string;
  icon: string;
  path: string;
}

export interface Place {
  id: string;
  name: string;
  address: string;
  city: string;
  lat?: number;
  lng?: number;
  theme: CityTheme;
  kind: 'hotel' | 'meeting' | 'station' | 'restaurant' | 'sight';
  notes?: string;
  phone?: string;
  website?: string;
}

export interface TripEvent {
  id: string;
  title: string;
  dateLabel: string;
  dayId: string;
  start: string;
  end: string;
  location?: string;
  address?: string;
  details?: string;
  theme: CityTheme;
  mapQuery?: string;
  website?: string;
}

export interface DontMissEntry {
  text: string;
  address?: string;
  mapQuery?: string;
}

export interface TimelineDay {
  id: string;
  date: string;
  weekday: string;
  where: string;
  highlights: string;
  theme: CityTheme;
  image: string;
  freeTime?: string;
  dontMiss: DontMissEntry[];
  rain?: string;
  rainMapQuery?: string;
}

export function dm(text: string, mapQuery?: string, address?: string): DontMissEntry {
  return {
    text,
    address,
    mapQuery: mapQuery ?? address,
  };
}

export interface Phrase {
  foreign: string;
  sounds: string;
  english: string;
  speakLang?: 'en-US';
}

export interface TravelApp {
  name: string;
  why: string;
  packageId?: string;
  webUrl?: string;
  owners?: string;
}

export const APP_TITLE = 'Boston 2026';
export const APP_SUBTITLE = 'Aug 8 – 12 · Downtown · North End · Harbor · Cambridge';

/** Bundled hero photos in public/images — Boston-themed, works offline */
export const CITY_IMAGES: Record<CityTheme, string> = {
  boston: '/images/boston.jpg',
  northend: '/images/northend.jpg',
  harbor: '/images/harbor.jpg',
  cambridge: '/images/cambridge.jpg',
  sports: '/images/sports.jpg',
  travel: '/images/travel.jpg',
  food: '/images/food.jpg',
  fallout: '/images/boston.jpg',
};

/** Family / travel group — edit names here; also editable in Expenses */
export const TRIP_MEMBERS = ['Steve', 'Sue', 'Kristen', 'Connor', 'Lauren', 'Jecen'] as const;

export type TripMemberName = (typeof TRIP_MEMBERS)[number];

export const EXPENSE_CATEGORIES = [
  'Meals',
  'Hotel',
  'Transport',
  'Activities',
  'Groceries',
  'Other',
] as const;

export const NAV_SECTIONS: NavSection[] = [
  { id: 'home', label: 'Home', icon: 'home', path: '/' },
  { id: 'itinerary', label: 'Itinerary', icon: 'days', path: '/itinerary' },
  { id: 'calendar', label: 'Calendar', icon: 'calendar', path: '/calendar' },
  { id: 'weather', label: 'Weather', icon: 'sun', path: '/weather' },
  { id: 'places', label: 'Maps', icon: 'maps', path: '/places' },
  { id: 'dontmiss', label: "Don't Miss", icon: 'star', path: '/dont-miss' },
  { id: 'bingo', label: 'Trip Bingo', icon: 'bingo', path: '/bingo' },
  { id: 'expenses', label: 'Expenses', icon: 'wallet', path: '/expenses' },
  { id: 'journal', label: 'Journal', icon: 'journal', path: '/journal' },
  { id: 'fallout', label: 'Fallout Boston', icon: 'fallout', path: '/fallout' },
  { id: 'setup', label: 'Before You Go', icon: 'setup', path: '/setup' },
  { id: 'quick', label: 'Quick Ref', icon: 'ticket', path: '/quick-ref' },
  { id: 'emergency', label: 'Emergency', icon: 'emergency', path: '/emergency' },
  { id: 'more', label: 'More', icon: 'book', path: '/more' },
];

export const HOTELS: Place[] = [
  {
    id: 'hotel-boston',
    name: 'Newbury Guest House',
    address: '261 Newbury St, Boston, MA 02116, USA',
    city: 'Boston',
    lat: 42.3493,
    lng: -71.0827,
    theme: 'boston',
    kind: 'hotel',
    notes: 'Back Bay · check-in 3 PM · check-out noon',
    phone: '+1 617-670-6000',
    website: 'https://www.newburyguesthouse.com/',
  },
];

export const MEETING_POINTS: Place[] = [
  {
    id: 'station-logan',
    name: 'Boston Logan International Airport',
    address: '1 Harborside Dr, Boston, MA 02128',
    city: 'Boston',
    lat: 42.3656,
    lng: -71.0096,
    theme: 'travel',
    kind: 'station',
    notes: 'Silver Line SL1 to South Station · ~20–30 min to downtown',
    website: 'https://www.massport.com/logan-airport/',
  },
  {
    id: 'station-south',
    name: 'South Station',
    address: '700 Atlantic Ave, Boston, MA 02110',
    city: 'Boston',
    lat: 42.3523,
    lng: -71.0552,
    theme: 'travel',
    kind: 'station',
    notes: 'Amtrak · MBTA Red Line · bus terminal',
    website: 'https://www.amtrak.com/stations/bos',
  },
  {
    id: 'station-back-bay',
    name: 'Back Bay Station',
    address: '145 Dartmouth St, Boston, MA 02116',
    city: 'Boston',
    lat: 42.3473,
    lng: -71.0755,
    theme: 'travel',
    kind: 'station',
    notes: 'Amtrak & commuter rail',
    website: 'https://www.amtrak.com/stations/bby',
  },
  {
    id: 'station-north',
    name: 'North Station',
    address: '120 Causeway St, Boston, MA 02114',
    city: 'Boston',
    lat: 42.3656,
    lng: -71.0617,
    theme: 'travel',
    kind: 'station',
    notes: 'Amtrak Downeaster · Lauren & Jecen Sat arrive / Tue depart · Orange & Green Lines · TD Garden',
    website: 'https://www.amtrak.com/stations/bon',
  },
  {
    id: 'meet-north-end-food',
    name: 'North End Food Tour — meetup',
    address: '156 Blackstone St, Boston, MA 02109',
    city: 'Boston',
    lat: 42.3618,
    lng: -71.0568,
    theme: 'northend',
    kind: 'meeting',
    notes:
      'Booking YPWZK2GG · Mon 10 Aug 12:00 PM · 6 travellers (Susan Stefanovich) · Meet outside RMV / Boston Public Market entrance · arrive 5–10 min early · Glass-and-metal awning by Surface Rd & Hanover St bus stop · MassDOT sign',
    website: 'https://www.urbanadventures.com/en/boston/boston-north-end-food-tour',
  },
  {
    id: 'meet-old-town-trolley',
    name: 'Old Town Trolley — Copley Square stop',
    address: 'Copley Square / Trinity Church, Boston, MA 02116',
    city: 'Boston',
    lat: 42.3499,
    lng: -71.0765,
    theme: 'boston',
    kind: 'meeting',
    notes:
      'Sun 9 Aug · all-day hop-on hop-off · start at any stop · nearest to hotel: Copley Square / Trinity Church (Back Bay loop) · show ticket to conductor',
    website: 'https://www.trolleytours.com/boston',
  },
];

export const RESTAURANTS: Place[] = [
  {
    id: 'dine-union',
    name: 'Union Oyster House',
    address: '41 Union St, Boston, MA 02108',
    city: 'Boston',
    lat: 42.3612,
    lng: -71.0567,
    theme: 'food',
    kind: 'restaurant',
    notes: 'America\'s oldest restaurant · chowder & oysters',
    website: 'https://www.unionoysterhouse.com/',
  },
  {
    id: 'dine-regina',
    name: 'Regina Pizzeria (North End)',
    address: '11½ Thacher St, Boston, MA 02113',
    city: 'Boston',
    lat: 42.3651,
    lng: -71.055,
    theme: 'northend',
    kind: 'restaurant',
    notes: 'Cash preferred · expect a line',
    website: 'https://www.pizzeriaregina.com/',
  },
  {
    id: 'dine-mike',
    name: 'Mike\'s Pastry',
    address: '300 Hanover St, Boston, MA 02113',
    city: 'Boston',
    lat: 42.3658,
    lng: -71.0545,
    theme: 'northend',
    kind: 'restaurant',
    notes: 'Cannoli classic · Hanover St',
    website: 'https://www.mikespastry.com/',
  },
  {
    id: 'dine-legal',
    name: 'Legal Sea Foods (Harbor)',
    address: '255 State St, Boston, MA 02109',
    city: 'Boston',
    lat: 42.3592,
    lng: -71.0515,
    theme: 'harbor',
    kind: 'restaurant',
    notes: 'Clam chowder & lobster roll',
    website: 'https://www.legalseafoods.com/',
  },
  {
    id: 'dine-atlantic',
    name: 'Atlantic Fish',
    address: '761 Boylston St, Boston, MA 02116',
    city: 'Boston',
    lat: 42.3485,
    lng: -71.082,
    theme: 'food',
    kind: 'restaurant',
    notes: 'Sat 8 Aug · reservation 7–9 PM Boston (6–8 PM CDT)',
    website: 'https://www.atlanticfish.com/',
  },
  {
    id: 'dine-piattini',
    name: 'Piattini',
    address: '226 Newbury St, Boston, MA 02116',
    city: 'Boston',
    lat: 42.3498,
    lng: -71.0815,
    theme: 'food',
    kind: 'restaurant',
    notes: 'Sun 9 Aug · reservation 7–9 PM Boston (6–8 PM CDT)',
    website: 'https://www.piattinirestaurant.com/',
  },
  {
    id: 'dine-krasi',
    name: 'Krasi',
    address: '48 Gloucester St, Boston, MA 02115',
    city: 'Boston',
    lat: 42.3489,
    lng: -71.0845,
    theme: 'food',
    kind: 'restaurant',
    notes: 'Mon 10 Aug · reservation 6–8 PM · Greek meze & wine bar',
    phone: '+1 617-536-0230',
    website: 'https://www.krasiboston.com/',
  },
  {
    id: 'dine-la-voile',
    name: 'La Voile',
    address: '261 Newbury St, Boston, MA 02116',
    city: 'Boston',
    lat: 42.3493,
    lng: -71.0827,
    theme: 'food',
    kind: 'restaurant',
    notes: 'Tue 11 Aug · table for 4 at 7:00 PM · Steve, Sue, Connor, Kristen · French brasserie · same building as guest house',
    phone: '+1 617-587-4200',
    website: 'https://www.lavoilerestaurants.com/la-voile-newbury/',
  },
];

export const SIGHTS: Place[] = [
  {
    id: 'sight-common',
    name: 'Boston Common',
    address: '139 Tremont St, Boston, MA 02111',
    city: 'Boston',
    lat: 42.3551,
    lng: -71.0657,
    theme: 'boston',
    kind: 'sight',
    notes: 'Start of the Freedom Trail',
    website: 'https://www.boston.gov/parks/boston-common',
  },
  {
    id: 'sight-garden',
    name: 'Public Garden & Swan Boats',
    address: '4 Charles St, Boston, MA 02116',
    city: 'Boston',
    lat: 42.3541,
    lng: -71.0707,
    theme: 'boston',
    kind: 'sight',
    website: 'https://www.boston.gov/parks/public-garden',
  },
  {
    id: 'sight-faneuil',
    name: 'Faneuil Hall & Quincy Market',
    address: '4 S Market St, Boston, MA 02109',
    city: 'Boston',
    lat: 42.3602,
    lng: -71.0549,
    theme: 'boston',
    kind: 'sight',
    website: 'https://www.nps.gov/bost/planyourvisit/faneuil-hall.htm',
  },
  {
    id: 'sight-aquarium',
    name: 'New England Aquarium',
    address: '1 Central Wharf, Boston, MA 02110',
    city: 'Boston',
    lat: 42.3591,
    lng: -71.0498,
    theme: 'harbor',
    kind: 'sight',
    website: 'https://www.neaq.org/',
  },
  {
    id: 'sight-constitution',
    name: 'USS Constitution & Museum',
    address: 'Building 22, Charlestown Navy Yard, Boston, MA 02129',
    city: 'Boston',
    lat: 42.3725,
    lng: -71.0566,
    theme: 'harbor',
    kind: 'sight',
    notes: 'Free tours · arrive early',
    website: 'https://ussconstitutionmuseum.org/',
  },
  {
    id: 'sight-fenway',
    name: 'Fenway Park',
    address: '4 Jersey St, Boston, MA 02215',
    city: 'Boston',
    lat: 42.3467,
    lng: -71.0972,
    theme: 'sports',
    kind: 'sight',
    notes: 'Tour or game if in season',
    website: 'https://www.mlb.com/redsox/ballpark',
  },
  {
    id: 'sight-harvard',
    name: 'Harvard Yard',
    address: 'Cambridge, MA 02138',
    city: 'Cambridge',
    lat: 42.3744,
    lng: -71.1182,
    theme: 'cambridge',
    kind: 'sight',
    website: 'https://www.harvard.edu/visit/',
  },
  {
    id: 'sight-mit',
    name: 'MIT Stata Center area',
    address: '32 Vassar St, Cambridge, MA 02139',
    city: 'Cambridge',
    lat: 42.3616,
    lng: -71.0906,
    theme: 'cambridge',
    kind: 'sight',
    website: 'https://www.mit.edu/visit/',
  },
];

export const TIMELINE: TimelineDay[] = [
  {
    id: 'aug8',
    date: 'Sat 8 Aug',
    weekday: 'Saturday',
    where: 'Boston — arrive',
    highlights: 'UA1606 lands 2:02 PM · Lauren & Jecen train 2:20 PM · Atlantic Fish 7 PM',
    theme: 'travel',
    image: CITY_IMAGES.travel,
    freeTime: 'Flights/train ~2 PM · hotel check-in 3 PM · dinner 7 PM',
    dontMiss: [
      dm('★ UA1606 lands at Logan 2:02 PM', 'Boston Logan Airport', '1 Harborside Dr, Boston, MA 02128'),
      dm(
        '★ Lauren & Jecen — Downeaster arrives North Station 2:20 PM',
        'North Station Boston',
        '120 Causeway St, Boston, MA 02114'
      ),
      dm('★ Dinner — Atlantic Fish 7 PM', 'Atlantic Fish 761 Boylston St Boston', '761 Boylston St, Boston, MA 02116'),
    ],
    rain: 'Museum of Fine Arts or New England Aquarium',
    rainMapQuery: 'Museum of Fine Arts Boston',
  },
  {
    id: 'aug9',
    date: 'Sun 9 Aug',
    weekday: 'Sunday',
    where: 'Boston · hop-on trolley',
    highlights: 'Old Town Trolley all day · Piattini 7 PM',
    theme: 'boston',
    image: CITY_IMAGES.boston,
    freeTime: 'Trolley all day · dinner 7 PM',
    dontMiss: [
      dm(
        '★ Old Town Trolley — hop-on hop-off all day',
        'Old Town Trolley Copley Square Boston',
        'Copley Square / Trinity Church, Boston, MA 02116'
      ),
      dm('Nearest stop: Copley Square / Trinity Church', 'Trinity Church Copley Square Boston'),
      dm('★ Dinner — Piattini 7 PM', 'Piattini 226 Newbury St Boston', '226 Newbury St, Boston, MA 02116'),
    ],
    rain: 'Boston Tea Party Ships & Museum',
    rainMapQuery: 'Boston Tea Party Ships and Museum',
  },
  {
    id: 'aug10',
    date: 'Mon 10 Aug',
    weekday: 'Monday',
    where: 'North End · dinner',
    highlights: 'North End Food Tour 12 PM · Krasi dinner 6 PM',
    theme: 'northend',
    image: CITY_IMAGES.northend,
    freeTime: 'Food tour noon–~3 PM · dinner 6 PM',
    dontMiss: [
      dm(
        '★ North End Food Experience Tour 12 PM',
        'Boston RMV Service Center 156 Blackstone St',
        '156 Blackstone St, Boston, MA 02109'
      ),
      dm('Arrive 5–10 min early — show booking YPWZK2GG', 'Boston Public Market Blackstone St'),
      dm(
        '★ Dinner — Krasi 6–8 PM',
        'Krasi 48 Gloucester St Boston',
        '48 Gloucester St, Boston, MA 02115'
      ),
    ],
    rain: 'Institute of Contemporary Art',
    rainMapQuery: 'Institute of Contemporary Art Boston',
  },
  {
    id: 'aug11',
    date: 'Tue 11 Aug',
    weekday: 'Tuesday',
    where: 'Boston · dinner · train',
    highlights: 'Lauren & Jecen Downeaster 5:20 PM · La Voile 7:00 PM',
    theme: 'boston',
    image: CITY_IMAGES.boston,
    freeTime: 'Open day · train 5:20 PM · dinner 7:00 PM',
    dontMiss: [
      dm(
        '★ Lauren & Jecen — Downeaster departs North Station 5:20 PM',
        'North Station Boston',
        '120 Causeway St, Boston, MA 02114'
      ),
      dm(
        '★ Dinner — La Voile 7:00 PM (table for 4 · Steve, Sue, Connor, Kristen)',
        'La Voile 261 Newbury St Boston',
        '261 Newbury St, Boston, MA 02116'
      ),
    ],
    rain: 'Museum of Science',
    rainMapQuery: 'Museum of Science Boston',
  },
  {
    id: 'aug12',
    date: 'Wed 12 Aug',
    weekday: 'Wednesday',
    where: 'Depart Boston',
    highlights: 'Airport breakfast 8:45 AM · UA472 departs 12:27 PM',
    theme: 'travel',
    image: CITY_IMAGES.travel,
    freeTime: 'Check out early · breakfast 8:45 through security · UA472 12:27 PM',
    dontMiss: [
      dm('★ Check out (leave early for Logan)', 'Newbury Guest House', '261 Newbury St, Boston, MA 02116'),
      dm(
        '★ Breakfast 8:45 AM — Steve, Sue, Connor, Kristen (through security)',
        'Boston Logan Airport United',
        '1 Harborside Dr, Boston, MA 02128'
      ),
      dm('★ UA472 departs Logan 12:27 PM', 'Boston Logan Airport', '1 Harborside Dr, Boston, MA 02128'),
    ],
  },
];

/** Calendar times are Boston (Eastern) local — YYYYMMDDHHMMSS */
export const CALENDAR_EVENTS: TripEvent[] = [
  {
    id: 'e-ua1606',
    title: 'UA1606 — ORD → BOS',
    dateLabel: 'Sat 8 Aug',
    dayId: 'aug8',
    start: '20260808140200',
    end: '20260808153000',
    location: 'Boston Logan (BOS)',
    address: '1 Harborside Dr, Boston, MA 02128',
    details: 'United · departs Chicago ORD 10:25 AM Central · arrives BOS 2:02 PM',
    theme: 'travel',
    mapQuery: 'Boston Logan Airport United arrivals',
    website: 'https://www.united.com/',
  },
  {
    id: 'e-train-arrive',
    title: 'Downeaster arrives — Lauren & Jecen',
    dateLabel: 'Sat 8 Aug',
    dayId: 'aug8',
    start: '20260808142000',
    end: '20260808150000',
    location: 'North Station',
    address: '120 Causeway St, Boston, MA 02114',
    details: 'Lauren & Jecen · Amtrak Downeaster from New Hampshire · arrives North Station 2:20 PM',
    theme: 'travel',
    mapQuery: 'North Station Boston',
    website: 'https://www.amtrak.com/stations/bon',
  },
  {
    id: 'e-atlantic',
    title: 'Dinner — Atlantic Fish',
    dateLabel: 'Sat 8 Aug',
    dayId: 'aug8',
    start: '20260808190000',
    end: '20260808210000',
    location: 'Atlantic Fish',
    address: '761 Boylston St, Boston, MA 02116',
    details: 'Reservation 6–8 PM CDT · 7–9 PM Boston',
    theme: 'food',
    mapQuery: 'Atlantic Fish 761 Boylston St Boston',
    website: 'https://www.atlanticfish.com/',
  },
  {
    id: 'e-old-town-trolley',
    title: 'Old Town Trolley — hop-on hop-off',
    dateLabel: 'Sun 9 Aug',
    dayId: 'aug9',
    start: '20260809090000',
    end: '20260809170000',
    location: 'Old Town Trolley (any stop)',
    address: 'Copley Square / Trinity Church, Boston, MA 02116',
    details:
      'All-day hop-on hop-off · start at any stop · nearest to hotel: Copley Square / Trinity Church · show ticket to conductor · dinner Piattini 7 PM',
    theme: 'boston',
    mapQuery: 'Old Town Trolley Copley Square Boston',
    website: 'https://www.trolleytours.com/boston',
  },
  {
    id: 'e-piattini',
    title: 'Dinner — Piattini',
    dateLabel: 'Sun 9 Aug',
    dayId: 'aug9',
    start: '20260809190000',
    end: '20260809210000',
    location: 'Piattini',
    address: '226 Newbury St, Boston, MA 02116',
    details: 'Reservation 6–8 PM CDT · 7–9 PM Boston',
    theme: 'food',
    mapQuery: 'Piattini 226 Newbury St Boston',
    website: 'https://www.piattinirestaurant.com/',
  },
  {
    id: 'e-north-end-food',
    title: 'North End Food Experience Tour',
    dateLabel: 'Mon 10 Aug',
    dayId: 'aug10',
    start: '20260810120000',
    end: '20260810150000',
    location: 'Boston RMV Service Center',
    address: '156 Blackstone St, Boston, MA 02109',
    details:
      'Booking YPWZK2GG · Traveller Susan Stefanovich · 6 travellers · Meet outside RMV / Boston Public Market entrance · arrive 5–10 min early · show confirmation to guide',
    theme: 'northend',
    mapQuery: 'Boston RMV Service Center 156 Blackstone St',
    website: 'https://www.urbanadventures.com/en/boston/boston-north-end-food-tour',
  },
  {
    id: 'e-krasi',
    title: 'Dinner — Krasi',
    dateLabel: 'Mon 10 Aug',
    dayId: 'aug10',
    start: '20260810180000',
    end: '20260810200000',
    location: 'Krasi',
    address: '48 Gloucester St, Boston, MA 02115',
    details: 'Reservation 6–8 PM · Greek meze & wine bar · Back Bay',
    theme: 'food',
    mapQuery: 'Krasi 48 Gloucester St Boston',
    website: 'https://www.krasiboston.com/',
  },
  {
    id: 'e-la-voile',
    title: 'Dinner — La Voile',
    dateLabel: 'Tue 11 Aug',
    dayId: 'aug11',
    start: '20260811190000',
    end: '20260811210000',
    location: 'La Voile',
    address: '261 Newbury St, Boston, MA 02116',
    details: 'Table for 4 at 7:00 PM · Steve, Sue, Connor, Kristen · French brasserie · same building as Newbury Guest House',
    theme: 'food',
    mapQuery: 'La Voile 261 Newbury St Boston',
    website: 'https://www.lavoilerestaurants.com/la-voile-newbury/',
  },
  {
    id: 'e-train-depart',
    title: 'Downeaster departs — Lauren & Jecen',
    dateLabel: 'Tue 11 Aug',
    dayId: 'aug11',
    start: '20260811150000',
    end: '20260811152000',
    location: 'North Station',
    address: '120 Causeway St, Boston, MA 02114',
    details: 'Lauren & Jecen · Amtrak Downeaster to New Hampshire · departs North Station 5:20 PM · arrive station early',
    theme: 'travel',
    mapQuery: 'North Station Boston',
    website: 'https://www.amtrak.com/stations/bon',
  },
  {
    id: 'e-airport-breakfast',
    title: 'Airport breakfast (through security)',
    dateLabel: 'Wed 12 Aug',
    dayId: 'aug12',
    start: '20260812084500',
    end: '20260812094500',
    location: 'Boston Logan (BOS)',
    address: '1 Harborside Dr, Boston, MA 02128',
    details: 'Steve, Sue, Connor, Kristen · meet 8:45 AM already through security · before UA472',
    theme: 'food',
    mapQuery: 'Boston Logan Airport United',
    website: 'https://www.massport.com/logan-airport/',
  },
  {
    id: 'e-ua472',
    title: 'UA472 — BOS → ORD',
    dateLabel: 'Wed 12 Aug',
    dayId: 'aug12',
    start: '20260812100000',
    end: '20260812122700',
    location: 'Boston Logan (BOS)',
    address: '1 Harborside Dr, Boston, MA 02128',
    details: 'United · departs 12:27 PM · arrives ORD 2:20 PM Central · breakfast meetup 8:45 AM through security',
    theme: 'travel',
    mapQuery: 'Boston Logan Airport United departures',
    website: 'https://www.united.com/',
  },
];

export const QUICK_REF_SLANG = [
  { label: 'Wicked', slang: 'WICK-ed', meaning: 'Very (wicked good = really good)' },
  { label: 'Bang a U-ey', slang: 'bang a U-ay', meaning: 'Make a U-turn' },
  { label: 'The T', slang: 'the tee', meaning: 'MBTA subway' },
  { label: 'Packie', slang: 'PAK-ee', meaning: 'Liquor store' },
  { label: 'Bubbler', slang: 'BUB-ler', meaning: 'Drinking fountain' },
  { label: 'Jimmies', slang: 'JIM-eez', meaning: 'Chocolate sprinkles' },
  { label: 'Emergency', slang: '911', meaning: 'US emergency number' },
];

export const BOSTON_SLANG: Phrase[] = [
  { foreign: 'Wicked', sounds: 'WICK-ed', english: 'Very — "wicked good pizza"', speakLang: 'en-US' },
  { foreign: 'Wicked pissah', sounds: 'WICK-ed PISS-ah', english: 'Awesome / really great', speakLang: 'en-US' },
  { foreign: 'Bang a U-ey', sounds: 'bang a U-ay', english: 'Make a U-turn', speakLang: 'en-US' },
  { foreign: 'The T', sounds: 'the tee', english: 'The MBTA subway', speakLang: 'en-US' },
  { foreign: 'Packie', sounds: 'PAK-ee', english: 'Liquor store', speakLang: 'en-US' },
  { foreign: 'Bubbler', sounds: 'BUB-ler', english: 'Drinking fountain', speakLang: 'en-US' },
  { foreign: 'Jimmies', sounds: 'JIM-eez', english: 'Chocolate sprinkles on ice cream', speakLang: 'en-US' },
  { foreign: 'Down cellah', sounds: 'down SELL-ah', english: 'Down in the basement', speakLang: 'en-US' },
  { foreign: 'Clickah', sounds: 'CLICK-ah', english: 'Remote control', speakLang: 'en-US' },
  { foreign: 'Dunks', sounds: 'dunks', english: 'Dunkin\' (coffee run)', speakLang: 'en-US' },
  { foreign: 'No suh!', sounds: 'no suh', english: 'No way! / seriously?', speakLang: 'en-US' },
  { foreign: 'So don\'t I', sounds: 'so dont I', english: 'Me too / same here', speakLang: 'en-US' },
];

export const BOSTON_DISHES: Phrase[] = [
  { foreign: 'New England clam chowder', sounds: 'clam CHOW-dah', english: 'Creamy white clam soup — the classic', speakLang: 'en-US' },
  { foreign: 'Lobster roll', sounds: 'LOB-ster roll', english: 'Cold or buttered lobster on a split-top bun', speakLang: 'en-US' },
  { foreign: 'Fried clams', sounds: 'fried clams', english: 'Whole-belly clams, often with tartar sauce', speakLang: 'en-US' },
  { foreign: 'Boston cream pie', sounds: 'BOS-ton cream pie', english: 'Cake with custard & chocolate glaze (not a pie!)', speakLang: 'en-US' },
  { foreign: 'Cannoli', sounds: 'can-NO-lee', english: 'North End pastry — crisp shell, sweet ricotta', speakLang: 'en-US' },
  { foreign: 'North End Italian', sounds: 'north end', english: 'Red-sauce pasta, arancini, espresso', speakLang: 'en-US' },
  { foreign: 'Scrod', sounds: 'skrawd', english: 'Catch-of-the-day white fish, often baked', speakLang: 'en-US' },
  { foreign: 'Fenway frank', sounds: 'FEN-way frank', english: 'Ballpark hot dog at Fenway', speakLang: 'en-US' },
  { foreign: 'Sam Adams', sounds: 'sam AY-dams', english: 'Boston-born craft lager', speakLang: 'en-US' },
  { foreign: 'Fluffernutter', sounds: 'FLUFF-er-nut-ter', english: 'Marshmallow fluff & peanut butter sandwich', speakLang: 'en-US' },
];

export const TRAVEL_APPS: TravelApp[] = [
  { name: 'Google Maps', why: 'Offline map: Boston, Cambridge, Logan', owners: 'Everyone' },
  { name: 'MBTA mTicket', why: 'Commuter rail & ferry tickets', packageId: 'com.mbta.mobileapp', owners: 'Everyone' },
  { name: 'Uber / Lyft', why: 'Rides — Logan ↔ downtown', owners: 'Anyone' },
  { name: 'Bluebikes', why: 'Bike share stations citywide', webUrl: 'https://www.bluebikes.com/', owners: 'Optional' },
  { name: 'United', why: 'UA1606 & UA472 — mobile boarding pass & flight status', packageId: 'com.united.mobile.android', owners: 'Flight lead' },
  { name: 'OpenTable / Resy', why: 'North End & hot spots book up', owners: 'Dinner lead' },
  { name: 'WhatsApp / iMessage', why: 'Group chat on the trip', owners: 'Everyone' },
];

export const EMERGENCY = [
  { label: 'Emergency (US)', value: '911', tel: '911' },
  { label: 'Boston Police (non-emergency)', value: '617-343-4500', tel: '+16173434500' },
  { label: 'Mass General Hospital', value: '617-726-2000', tel: '+16177262000' },
  { label: 'Poison Control', value: '1-800-222-1222', tel: '+18002221222' },
];

export const ICON_CHECKLIST = {
  boston: ['Freedom Trail', 'Boston Common & Public Garden', 'Faneuil Hall / Quincy Market', 'Beacon Hill'],
  harbor: ['Harborwalk', 'USS Constitution', 'New England Aquarium', 'Tea Party Museum'],
  cambridge: ['Harvard Yard', 'MIT campus', 'Charles River walk'],
  sports: ['Fenway Park tour or game', 'Red Sox gear'],
};

export const USEFUL_LINKS = [
  { label: 'MBTA Trip Planner', url: 'https://www.mbta.com/trip-planner' },
  { label: 'Logan Airport (Massport)', url: 'https://www.massport.com/logan-airport/' },
  { label: 'Freedom Trail', url: 'https://www.thefreedomtrail.org/' },
  { label: 'Boston Visitor Guide', url: 'https://www.bostonusa.com/' },
  { label: 'Bluebikes', url: 'https://www.bluebikes.com/' },
  { label: 'Fenway Park tours', url: 'https://www.mlb.com/redsox/ballpark/tours' },
];
